import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/useAuth';
import { appelApi } from '../lib/api';
import { useToast } from '../components/ui/useToast';
import type { ContactMessagerie, ConversationResume, StatutMessagerie } from '../lib/useMessagerie';
import { ContexteReponseRapide, type PopupMessage } from '../messagerie/contexteReponseRapide';
import { jouerSonnerieMessage } from '../messagerie/sonnerieMessage';
import { ContexteAppel } from './contexte';
import { creerSonnerie } from './sonnerie';
import type { ContactAppel, EtatAppel, TypeAppel } from './types';

const NB_MAX_POPUPS = 3;

/**
 * Signalisation et connexion WebRTC des appels audio/visio.
 *
 * Monté une seule fois, dans `AppShell` (donc dès qu'un compte est
 * authentifié, peu importe l'écran affiché) : une sonnerie doit pouvoir
 * arriver alors qu'on est sur le tableau de bord, pas seulement sur
 * `/messages`. La connexion WebSocket et la `RTCPeerConnection` vivent ici,
 * pas dans un composant de page, pour ne pas couper l'appel en changeant de
 * route pendant sa durée.
 *
 * Aucun média ne passe par l'API : elle ne relaie que les messages de
 * contrôle (invitation, offre/réponse SDP, candidats ICE). La connexion
 * audio/vidéo elle-même est directe entre les deux navigateurs (ou via TURN
 * si configuré côté serveur), jamais stockée ni journalisée en contenu.
 */

const DELAI_RECONNEXION_MS = 3000;

type MessageSortant =
  | { type: 'invite'; conversationId: string; typeAppel: TypeAppel }
  | { type: 'accepter'; conversationId: string }
  | { type: 'refuser'; conversationId: string }
  | { type: 'raccrocher'; conversationId: string }
  | { type: 'offre'; conversationId: string; sdp: string }
  | { type: 'reponse'; conversationId: string; sdp: string }
  | { type: 'candidat-ice'; conversationId: string; candidat: unknown };

interface ServeurIce {
  urls: string | string[];
  username?: string;
  credential?: string;
}

export function FournisseurAppel({ children }: { children: ReactNode }): JSX.Element {
  const { utilisateur } = useAuth();
  const toast = useToast();
  const client = useQueryClient();

  const [etat, setEtat] = useState<EtatAppel>({ phase: 'inactif' });
  const [popups, setPopups] = useState<PopupMessage[]>([]);
  const [fluxLocal, setFluxLocal] = useState<MediaStream | null>(null);
  const [fluxDistant, setFluxDistant] = useState<MediaStream | null>(null);
  const [microCoupe, setMicroCoupe] = useState(false);
  const [cameraCoupee, setCameraCoupee] = useState(false);

  const socketRef = useRef<WebSocket | null>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const etatRef = useRef<EtatAppel>(etat);
  const fluxLocalRef = useRef<MediaStream | null>(null);
  const candidatsEnAttente = useRef<RTCIceCandidateInit[]>([]);
  const sonnerieRef = useRef(creerSonnerie());
  const demonteRef = useRef(false);

  etatRef.current = etat;

  /** Garde la ref synchrone avec l'état : `nettoyer` et les bascules micro/caméra
   * lisent la ref pour ne pas dépendre d'une fermeture périmée sur `fluxLocal`. */
  const definirFluxLocal = useCallback((flux: MediaStream | null) => {
    fluxLocalRef.current = flux;
    setFluxLocal(flux);
  }, []);

  const envoyer = useCallback((message: MessageSortant) => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify(message));
    }
  }, []);

  const fermerPopup = useCallback((conversationId: string) => {
    setPopups((precedent) => precedent.filter((p) => p.conversationId !== conversationId));
  }, []);

  const jouerSonnerie = useCallback((jouer: boolean) => {
    if (jouer) sonnerieRef.current.demarrer();
    else sonnerieRef.current.arreter();
  }, []);

  const nettoyer = useCallback(() => {
    jouerSonnerie(false);
    pcRef.current?.close();
    pcRef.current = null;
    candidatsEnAttente.current = [];
    fluxLocalRef.current?.getTracks().forEach((piste) => piste.stop());
    definirFluxLocal(null);
    setFluxDistant(null);
    setMicroCoupe(false);
    setCameraCoupee(false);
    setEtat({ phase: 'inactif' });
  }, [jouerSonnerie, definirFluxLocal]);

  const creerConnexionPair = useCallback(
    (conversationId: string, iceServers: ServeurIce[]): RTCPeerConnection => {
      const pc = new RTCPeerConnection({ iceServers });

      pc.onicecandidate = (evenement) => {
        if (evenement.candidate) {
          envoyer({ type: 'candidat-ice', conversationId, candidat: evenement.candidate.toJSON() });
        }
      };

      pc.ontrack = (evenement) => {
        setFluxDistant(evenement.streams[0] ?? null);
      };

      pc.onconnectionstatechange = () => {
        if (pc.connectionState === 'failed') {
          toast('Connexion d’appel perdue.');
          envoyer({ type: 'raccrocher', conversationId });
          nettoyer();
        }
      };

      return pc;
    },
    [envoyer, nettoyer, toast],
  );

  const obtenirServeursIce = useCallback(async (): Promise<ServeurIce[]> => {
    try {
      const { serveurs } = await appelApi<{ serveurs: ServeurIce[] }>('/appels/serveurs-ice');
      return serveurs;
    } catch {
      return [{ urls: 'stun:stun.l.google.com:19302' }];
    }
  }, []);

  const capturerMedia = useCallback(async (type: TypeAppel): Promise<MediaStream> => {
    return navigator.mediaDevices.getUserMedia({ audio: true, video: type === 'video' });
  }, []);

  /** Appelant : l'autre a accepté, on ouvre la connexion et on propose l'offre. */
  const demarrerOffre = useCallback(
    async (conversationId: string, type: TypeAppel) => {
      try {
        const [flux, serveursIce] = await Promise.all([capturerMedia(type), obtenirServeursIce()]);
        definirFluxLocal(flux);

        const pc = creerConnexionPair(conversationId, serveursIce);
        pcRef.current = pc;
        flux.getTracks().forEach((piste) => pc.addTrack(piste, flux));

        const offre = await pc.createOffer();
        await pc.setLocalDescription(offre);
        envoyer({ type: 'offre', conversationId, sdp: offre.sdp! });
      } catch {
        toast("Impossible d'accéder au micro/caméra pour l'appel.");
        envoyer({ type: 'raccrocher', conversationId });
        nettoyer();
      }
    },
    [capturerMedia, obtenirServeursIce, creerConnexionPair, definirFluxLocal, envoyer, nettoyer, toast],
  );

  /** Destinataire : après acceptation, prépare la connexion en attendant l'offre. */
  const preparerReception = useCallback(
    async (conversationId: string, type: TypeAppel) => {
      try {
        const [flux, serveursIce] = await Promise.all([capturerMedia(type), obtenirServeursIce()]);
        definirFluxLocal(flux);

        const pc = creerConnexionPair(conversationId, serveursIce);
        flux.getTracks().forEach((piste) => pc.addTrack(piste, flux));
        pcRef.current = pc;
      } catch {
        toast("Impossible d'accéder au micro/caméra pour l'appel.");
        envoyer({ type: 'refuser', conversationId });
        nettoyer();
      }
    },
    [capturerMedia, obtenirServeursIce, creerConnexionPair, definirFluxLocal, envoyer, nettoyer, toast],
  );

  const traiterOffreEntrante = useCallback(
    async (conversationId: string, sdp: string) => {
      const pc = pcRef.current;
      if (!pc) return;
      await pc.setRemoteDescription({ type: 'offer', sdp });
      for (const candidat of candidatsEnAttente.current) await pc.addIceCandidate(candidat);
      candidatsEnAttente.current = [];

      const reponse = await pc.createAnswer();
      await pc.setLocalDescription(reponse);
      envoyer({ type: 'reponse', conversationId, sdp: reponse.sdp! });
    },
    [envoyer],
  );

  const traiterReponseEntrante = useCallback(async (sdp: string) => {
    const pc = pcRef.current;
    if (!pc) return;
    await pc.setRemoteDescription({ type: 'answer', sdp });
    for (const candidat of candidatsEnAttente.current) await pc.addIceCandidate(candidat);
    candidatsEnAttente.current = [];
  }, []);

  const traiterCandidatEntrant = useCallback(async (candidat: RTCIceCandidateInit) => {
    const pc = pcRef.current;
    if (!pc || !pc.remoteDescription) {
      candidatsEnAttente.current.push(candidat);
      return;
    }
    await pc.addIceCandidate(candidat);
  }, []);

  // Connexion WebSocket de signalisation — ouverte tant qu'un compte est
  // authentifié, reconnectée automatiquement sur coupure réseau.
  useEffect(() => {
    if (!utilisateur) return;
    demonteRef.current = false;
    const sonnerie = sonnerieRef.current;

    function connecter(): void {
      if (demonteRef.current) return;
      const protocole = window.location.protocol === 'https:' ? 'wss' : 'ws';
      const socket = new WebSocket(`${protocole}://${window.location.host}/ws/appels`);
      socketRef.current = socket;

      socket.onmessage = (evenement) => {
        void (async () => {
          let message: Record<string, unknown>;
          try {
            message = JSON.parse(evenement.data as string);
          } catch {
            return;
          }
          const conversationId = message.conversationId as string | undefined;

          switch (message.type) {
            case 'sonnerie': {
              if (etatRef.current.phase !== 'inactif' || !conversationId) return;
              const appelant = message.appelant as ContactAppel | null;
              setEtat({
                phase: 'appel-entrant',
                conversationId,
                typeAppel: message.typeAppel as TypeAppel,
                appelant: appelant ?? { nom: '', prenom: 'Contact', initiales: '?' },
              });
              jouerSonnerie(true);
              return;
            }
            case 'presence': {
              // Diffusé par l'API dès qu'une connexion/déconnexion ou un
              // changement de statut survient : évite d'avoir à recharger la
              // page pour voir un contact passer en ligne ou hors ligne.
              const idPresence = message.utilisateurId as string | undefined;
              if (!idPresence) return;
              const presence = { enLigne: message.enLigne as boolean, statut: message.statut as StatutMessagerie };

              client.setQueryData<{ contacts: ContactMessagerie[] } | undefined>(
                ['messagerie', 'contacts'],
                (precedent) =>
                  precedent && {
                    contacts: precedent.contacts.map((c) => (c.id === idPresence ? { ...c, presence } : c)),
                  },
              );
              client.setQueryData<{ conversations: ConversationResume[] } | undefined>(
                ['messagerie', 'conversations'],
                (precedent) =>
                  precedent && {
                    conversations: precedent.conversations.map((conv) =>
                      conv.contact?.id === idPresence ? { ...conv, contact: { ...conv.contact, presence } } : conv,
                    ),
                  },
              );
              return;
            }
            case 'nouveau-message': {
              const idConversation = message.conversationId as string | undefined;
              const donnees = message.message as
                | { expediteur: { id: string; nom: string; prenom: string; initiales: string }; texte: string; envoyeLe: string; pieceJointe: unknown }
                | undefined;
              if (!idConversation || !donnees) return;
              jouerSonnerieMessage();
              // Déjà filtré côté API pour le « ne pas déranger » : ici on
              // évite seulement le doublon quand le fil concerné est déjà à
              // l'écran, où le message apparaît par le fil lui-même.
              if (window.location.pathname.startsWith('/messages')) return;
              const popup: PopupMessage = {
                conversationId: idConversation,
                expediteur: donnees.expediteur,
                texte: donnees.texte,
                envoyeLe: donnees.envoyeLe,
                aPieceJointe: Boolean(donnees.pieceJointe),
              };
              setPopups((precedent) => [popup, ...precedent.filter((p) => p.conversationId !== idConversation)].slice(0, NB_MAX_POPUPS));
              return;
            }
            case 'indisponible': {
              toast('Utilisateur indisponible pour le moment. Veuillez réessayer lorsque celui-ci sera présent.');
              nettoyer();
              return;
            }
            case 'expire': {
              // Reçu des deux côtés : l'appelant voit que personne n'a
              // répondu, l'appelé(e) voit que l'appel manqué s'est refermé —
              // sans ce second message, sa modale de sonnerie resterait
              // affichée indéfiniment après l'expiration côté serveur.
              toast(etatRef.current.phase === 'appel-entrant' ? 'Appel manqué.' : "Personne n'a répondu.");
              nettoyer();
              return;
            }
            case 'accepter': {
              const actuel = etatRef.current;
              if (actuel.phase !== 'appel-sortant' || !conversationId) return;
              setEtat({
                phase: 'en-cours',
                conversationId,
                typeAppel: actuel.typeAppel,
                contact: actuel.contact,
                debut: Date.now(),
                minimise: false,
              });
              void demarrerOffre(conversationId, actuel.typeAppel);
              return;
            }
            case 'refuser': {
              toast('Appel refusé.');
              nettoyer();
              return;
            }
            case 'raccrocher': {
              nettoyer();
              return;
            }
            case 'offre': {
              if (conversationId) void traiterOffreEntrante(conversationId, message.sdp as string);
              return;
            }
            case 'reponse': {
              void traiterReponseEntrante(message.sdp as string);
              return;
            }
            case 'candidat-ice': {
              void traiterCandidatEntrant(message.candidat as RTCIceCandidateInit);
              return;
            }
            default:
              return;
          }
        })();
      };

      socket.onclose = () => {
        // Ne reconnecte que si cette socket est toujours la référence active :
        // en mode strict de développement, React monte-démonte-remonte l'effet
        // avant même que la fermeture de l'ancienne socket ait eu le temps de
        // se déclencher. Sans cette vérification, l'ancienne socket
        // programmait une reconnexion concurrente à celle déjà ouverte par le
        // remontage — deux (ou trois) sockets vivantes pour le même compte,
        // chacune relayant les mêmes messages du serveur.
        if (socketRef.current !== socket) return;
        socketRef.current = null;
        if (!demonteRef.current) setTimeout(connecter, DELAI_RECONNEXION_MS);
      };
    }

    connecter();

    return () => {
      demonteRef.current = true;
      socketRef.current?.close();
      socketRef.current = null;
      sonnerie.arreter();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [utilisateur?.id]);

  const appeler = useCallback(
    (conversationId: string, contact: ContactAppel, typeAppel: TypeAppel) => {
      if (etatRef.current.phase !== 'inactif') {
        toast('Un appel est déjà en cours.');
        return;
      }
      setEtat({ phase: 'appel-sortant', conversationId, typeAppel, contact });
      envoyer({ type: 'invite', conversationId, typeAppel });
    },
    [envoyer, toast],
  );

  const accepter = useCallback(() => {
    const actuel = etatRef.current;
    if (actuel.phase !== 'appel-entrant') return;
    jouerSonnerie(false);
    envoyer({ type: 'accepter', conversationId: actuel.conversationId });
    setEtat({
      phase: 'en-cours',
      conversationId: actuel.conversationId,
      typeAppel: actuel.typeAppel,
      contact: actuel.appelant,
      debut: Date.now(),
      minimise: false,
    });
    void preparerReception(actuel.conversationId, actuel.typeAppel);
  }, [envoyer, jouerSonnerie, preparerReception]);

  const refuser = useCallback(() => {
    const actuel = etatRef.current;
    if (actuel.phase !== 'appel-entrant') return;
    envoyer({ type: 'refuser', conversationId: actuel.conversationId });
    nettoyer();
  }, [envoyer, nettoyer]);

  const raccrocher = useCallback(() => {
    const actuel = etatRef.current;
    if (actuel.phase === 'inactif') return;
    envoyer({ type: 'raccrocher', conversationId: actuel.conversationId });
    nettoyer();
  }, [envoyer, nettoyer]);

  const minimiser = useCallback(() => {
    setEtat((precedent) => (precedent.phase === 'en-cours' ? { ...precedent, minimise: true } : precedent));
  }, []);

  const agrandir = useCallback(() => {
    setEtat((precedent) => (precedent.phase === 'en-cours' ? { ...precedent, minimise: false } : precedent));
  }, []);

  const basculerMicro = useCallback(() => {
    setMicroCoupe((precedent) => {
      const suivant = !precedent;
      fluxLocal?.getAudioTracks().forEach((piste) => (piste.enabled = !suivant));
      return suivant;
    });
  }, [fluxLocal]);

  const basculerCamera = useCallback(() => {
    setCameraCoupee((precedent) => {
      const suivant = !precedent;
      fluxLocal?.getVideoTracks().forEach((piste) => (piste.enabled = !suivant));
      return suivant;
    });
  }, [fluxLocal]);

  return (
    <ContexteAppel.Provider
      value={{
        etat,
        fluxLocal,
        fluxDistant,
        microCoupe,
        cameraCoupee,
        appeler,
        accepter,
        refuser,
        raccrocher,
        minimiser,
        agrandir,
        basculerMicro,
        basculerCamera,
      }}
    >
      <ContexteReponseRapide.Provider value={{ popups, fermer: fermerPopup }}>
        {children}
      </ContexteReponseRapide.Provider>
    </ContexteAppel.Provider>
  );
}
